"use client";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ArrowUpRight, Link as LinkIcon, Share2 } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { usePostHog } from "posthog-js/react";
import { IMAGE_SIZES } from "@/lib/imageSizes";

type Props = {
  title: string;
  url: string;
  id: string;
};

const ListingShareMenu: React.FC<Props> = ({ title, url, id }) => {
  const t = useTranslations("listing");
  const posthog = usePostHog();
  const [open, setOpen] = useState(false);

  const emailSubject = `${title} - Luntero`;
  const body = t("share.title-url-text", { title, url });

  const emailShareUrl = `mailto:?subject=${encodeURIComponent(
    emailSubject,
  )}&body=${encodeURIComponent(body)}`;
  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(body)}`;

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const isNativeShareSupported =
    mounted && typeof navigator !== "undefined" && !!navigator.share;

  const captureEvent = (name: string) => {
    posthog.capture("share_listing", {
      type: name,
      listing_id: id,
    });
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        captureEvent("NATIVE_SHARE");
        setOpen(false);
        await navigator.share({ title, text: body, url });
      } catch (error) {
        console.error("Native share failed:", error);
      }
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(url);
    captureEvent("LINK");
    setOpen(false);
    toast.info(t("share.link-copy"));
  };

  const openShareWindow = (shareUrl: string) => {
    setOpen(false);
    window.open(shareUrl, "_blank");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button aria-label={t("share.title")} variant="outline" size="sm">
          <Share2 className="size-4" />
          <span>{t("share.button")}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-56 flex-col gap-1 p-2">
        <ShareOption
          label={t("share.copy-link")}
          icon={<LinkIcon className="size-4" />}
          onClick={handleCopyLink}
        />
        <ShareOption
          label="Email"
          icon={
            <Image
              src="/assets/other/social/mail.webp"
              alt="Email"
              width={100}
              height={100}
              unoptimized
              sizes={IMAGE_SIZES.icon20}
              className="w-4 h-auto"
            />
          }
          onClick={() => {
            captureEvent("EMAIL");
            openShareWindow(emailShareUrl);
          }}
        />
        <ShareOption
          label="WhatsApp"
          icon={
            <Image
              src="/assets/other/social/whatsapp.webp"
              alt="WhatsApp"
              width={100}
              height={100}
              unoptimized
              sizes={IMAGE_SIZES.icon20}
              className="w-4 h-auto"
            />
          }
          onClick={() => {
            captureEvent("WHATSAPP");
            openShareWindow(whatsappShareUrl);
          }}
        />
        {isNativeShareSupported && (
          <ShareOption
            label={t("share.see-more")}
            icon={<ArrowUpRight className="size-4" />}
            onClick={handleNativeShare}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ListingShareMenu;

type ShareOptionProps = {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
};

const ShareOption: React.FC<ShareOptionProps> = ({ label, icon, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full flex-row items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
    >
      <span className="flex size-5 items-center justify-center text-muted-foreground">
        {icon}
      </span>
      <span>{label}</span>
    </button>
  );
};
